import { API_ENDPOINTS } from '@/api/endpoints';
import { fetchSubprojects } from '@/services/subprojectService';
import { SubprojectBuildingDetails } from '@/stores/SubprojectStore';
import { apiFetch } from '@/util/apiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useGetSubproject(orgId?: string, subProjId?: string) {
  return useQuery({
    queryKey: ['subproject', subProjId],
    queryFn: async () => {
      if (!orgId || !subProjId) {
        // this error should never throw because the queryFn is only enabled when orgId
        // and subProjId is present
        throw new Error("ID's must be defined");
      }
      return apiFetch<SubprojectBuildingDetails>(
        API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DETAIL(orgId, subProjId),
      );
    },
    enabled: !!orgId && !!subProjId,
  });
}

export function useGetProjectSubprojects(orgId?: string, projectId?: string) {
  return useQuery({
    queryKey: ['project-subprojects', orgId, projectId],
    queryFn: async () => {
      if (!orgId || !projectId) {
        // never throws: the query is only enabled when both ids are present
        throw new Error("ID's must be defined");
      }
      return fetchSubprojects(projectId, orgId);
    },
    enabled: !!orgId && !!projectId,
  });
}

export function useUpdateSubprojectDetails(orgId: string, subProjId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subproject: SubprojectBuildingDetails) => {
      return apiFetch(
        API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DETAIL(orgId, subProjId),
        {
          method: 'PUT',
          body: JSON.stringify(subproject),
        },
      );
    },
    onSuccess: async () => {
      // Invalidate the specific subproject query
      await queryClient.invalidateQueries({
        queryKey: ['subproject', subProjId],
      });
      // Also invalidate projects queries to update the landing page immediately
      await queryClient.invalidateQueries({
        queryKey: ['projects'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['projects-with-subprojects'],
      });
    },
    onError: (error: Error) => {
      console.error('Failed to update subproject:', error);
      // Re-throw so calling component can handle it
      throw error;
    },
  });
}
